import React from 'react';
import { Shield, Lock, Eye, Database, ArrowLeft } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-black text-white p-8 lg:p-20 animate-fade-in">
      <div className="max-w-4xl mx-auto space-y-12">
        {onBack && (
          <button 
            onClick={onBack}
            className="text-slate-500 hover:text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 mb-8"
          >
            <ArrowLeft className="w-3 h-3" /> Voltar
          </button>
        )}

        <div className="space-y-4">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400">
            <Lock className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-widest">Privacidade de Dados</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold tracking-tighter">Política de Privacidade</h1>
          <p className="text-slate-500 font-medium">Última atualização: 26 de Março de 2026</p>
        </div>

        <div className="premium-card p-10 space-y-10 text-slate-300 leading-relaxed text-sm">
          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Eye className="w-5 h-5 text-emerald-500" />
              1. COLETA DE INFORMAÇÕES
            </h2>
            <p>
              A OptiVelo coleta informações necessárias para a prestação dos serviços de otimização, incluindo:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Dados Cadastrais:</strong> Nome, e-mail e informações de pagamento fornecidas durante a assinatura.</li>
              <li><strong>Dados de Uso:</strong> Informações sobre como você utiliza a plataforma e interage com nossas ferramentas de IA.</li>
              <li><strong>Dados de Visitantes:</strong> Através de nossos scripts, coletamos dados anônimos de comportamento dos visitantes em suas páginas para fins de análise e otimização.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Database className="w-5 h-5 text-emerald-500" />
              2. USO DOS DADOS
            </h2>
            <p>
              Utilizamos seus dados para:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Prover e manter as funcionalidades da plataforma OptiVelo.</li>
              <li>Personalizar as recomendações da nossa Inteligência Artificial.</li>
              <li>Processar pagamentos e gerenciar assinaturas.</li>
              <li>Enviar comunicações importantes sobre o serviço e atualizações.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Shield className="w-5 h-5 text-emerald-500" />
              3. SEGURANÇA E LGPD
            </h2>
            <p>
              Estamos totalmente comprometidos com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong>. Implementamos criptografia de ponta a ponta e protocolos de segurança rigorosos para garantir que seus dados e os dados de seus clientes nunca sejam acessados por terceiros não autorizados.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Lock className="w-5 h-5 text-emerald-500" />
              4. COMPARTILHAMENTO DE DADOS
            </h2>
            <p>
              A OptiVelo <strong>NÃO vende</strong> seus dados para terceiros. O compartilhamento ocorre apenas com parceiros essenciais para a operação (ex: processadores de pagamento como Stripe/Mercado Pago e serviços de infraestrutura de nuvem), sempre sob contratos de confidencialidade.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-3">
              <Shield className="w-5 h-5 text-emerald-500" />
              5. SEUS DIREITOS
            </h2>
            <p>
              Você tem o direito de acessar, corrigir ou excluir seus dados pessoais a qualquer momento através das configurações da sua conta ou entrando em contato com nosso suporte.
            </p>
          </section>
        </div>

        <div className="text-center pb-20">
          <p className="text-slate-500 text-xs italic">
            Sua confiança é o nosso maior ativo. Protegemos seus dados como se fossem nossos.
          </p>
          <p className="text-slate-500 text-[10px] mt-4 uppercase tracking-widest font-bold">© 2026 OptiVelo</p>
        </div>
      </div>
    </div>
  );
};
